import { AssetDetail } from '../../../types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { Building2, HelpCircle } from 'lucide-react'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typogaphy } from 'vnetwork-ui'
import { formatDateWithFormatter } from '@/utils/function'

interface OverviewTabProps {
    asset: AssetDetail
}

const OverviewTab = ({ asset }: OverviewTabProps) => {
    const { t } = useTranslation('asset')

    const getDocumentGroupLabel = (value: string) => {
        return t(`document_groups.${value}`) || value
    }

    const getDocumentTypeLabel = (value: string) => {
        return t(`document_types.${value}`) || value
    }

    const getConfidentialityLabel = (value: string) => {
        return t(`confidentiality_levels.${value}`) || value
    }

    const getIntegrityLabel = (value: string) => {
        return t(`integrity_levels.${value}`) || value
    }

    const getAvailabilityLabel = (value: string) => {
        return t(`availability_levels.${value}`) || value
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Card className="border-none">
                <CardHeader>
                    <CardTitle>
                        <Typogaphy className="font-semibold text-primary mb-2">
                            {t('overview.generalInfo')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                    <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm font-medium text-gray-500">
                            {t('overview.documentGroup')}
                        </span>
                        <span className="text-sm">
                            {getDocumentGroupLabel(asset.document_group)}
                        </span>
                    </div>
                    {asset.current_version && (
                        <div className="grid grid-cols-2 gap-2">
                            <span className="text-sm font-medium text-gray-500">
                                {t('overview.documentType')}
                            </span>
                            <span className="text-sm">
                                {getDocumentTypeLabel(
                                    asset.current_version.document_type
                                )}
                            </span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm font-medium text-gray-500">
                            {t('overview.department')}
                        </span>
                        <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">
                                {asset.department_name}
                            </span>
                        </div>
                    </div>
                    {!asset.current_version.is_read_confirmation_required && (
                        <div className="grid grid-cols-2 gap-2">
                            <span className="text-sm font-medium text-gray-500">
                                {t('form.readConfirmationRequired')}
                            </span>

                            <div className="flex items-center gap-1">
                                <span className="text-sm font-semibold text-gray-900">
                                    {t('common.yes')}
                                </span>
                                <TooltipProvider>
                                    <Tooltip delayDuration={300}>
                                        <TooltipTrigger asChild>
                                            <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p className="max-w-xs">
                                                {t(
                                                    'form.readConfirmationTooltip'
                                                )}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    )}
                </CardContent>
                {asset.current_version && (
                    <div className="px-6 pb-6 pt-0">
                        <div className="border-t pt-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">
                                {t('labels.effectiveDate')}
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">
                                        {t('labels.fromDate')}
                                    </p>
                                    <p className="text-md font-semibold text-gray-900">
                                        {asset.current_version.start_date
                                            ? formatDateWithFormatter(
                                                  asset.current_version
                                                      .start_date,
                                                  'DD/MM/YYYY'
                                              )
                                            : '---'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">
                                        {t('labels.toDate')}
                                    </p>
                                    <p className="text-md font-semibold text-gray-900">
                                        {asset.current_version.end_date
                                            ? formatDateWithFormatter(
                                                  asset.current_version
                                                      .end_date,
                                                  'DD/MM/YYYY'
                                              )
                                            : '---'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            <Card className="border-none">
                <CardHeader>
                    <CardTitle>
                        <Typogaphy className="font-semibold text-primary mb-2">
                            {t('overview.securityClassification')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">
                    {asset.current_version && (
                        <>
                            <div className="grid grid-cols-2 gap-2">
                                <span className="text-sm font-medium text-gray-500">
                                    {t('overview.confidentiality')}
                                </span>
                                <span className="text-sm">
                                    {getConfidentialityLabel(
                                        asset.current_version
                                            .confidentiality_level
                                    )}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <span className="text-sm font-medium text-gray-500">
                                    {t('overview.integrity')}
                                </span>
                                <span className="text-sm">
                                    {getIntegrityLabel(
                                        asset.current_version.integrity_level
                                    )}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <span className="text-sm font-medium text-gray-500">
                                    {t('overview.availability')}
                                </span>
                                <span className="text-sm">
                                    {getAvailabilityLabel(
                                        asset.current_version.availability_level
                                    )}
                                </span>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
            <Card className="md:col-span-2 border-none">
                <CardHeader>
                    <CardTitle>
                        <Typogaphy className="font-semibold text-primary mb-2">
                            {t('overview.description')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {asset.asset_description}
                    </p>
                </CardContent>
            </Card>
        </div>
    )
}

export default OverviewTab
